import { UAParser } from 'ua-parser-js';

import type { ScannedPairingDeviceInfo } from '../../api';
import { log } from '../../api/log';
import { ScannedDeviceType } from '../../api/portal-adapter';
import { ClinicalUnit, DeviceType, ScannerType, SupportedDeviceType } from '../../types';
import { idbDel, idbGet, idbSet } from '../../util/indexedStorage';

const LAST_SELECTION_STORAGE_KEY = 'qarescan.additional-info.last-selection.v1';
const osName = new UAParser().getOS().name || '';

export const isMobileOS = ['Android', 'Android-x86', 'iOS'].includes(osName);
export const availableScannerTypes = [
  ScannerType.Manual,
  isMobileOS ? ScannerType.Camera : ScannerType.Handheld,
];
export const DEFAULT_SUPPORTED_DEVICE_TYPES: SupportedDeviceType[] = [
  DeviceType.Healthdot,
  DeviceType.ViQtor,
];
export const WIZARD_STEP = {
  PATIENT: 1,
  DEVICE: 2,
  ADDITIONAL_INFO: 3,
  ACTIVATE_HEALTHDOT: 4,
} as const;
export const VIQTOR_MANUAL_SERIAL_FORMAT = '^[A-Za-z0-9-]{4,40}$';
export const VIQTOR_BATTERY_WARNING_THRESHOLD = 20;

export type WizardStep = (typeof WIZARD_STEP)[keyof typeof WIZARD_STEP];

export type RememberedSelection = {
  clinicalUnitId?: string;
  cohortId?: string;
};

export type ViQtorStatusWarningReason = 'LOW_BATTERY' | 'NO_CONNECTION';

export type PendingViQtorStatusConfirmation = {
  deviceId: string;
  deviceType: DeviceType;
  deviceStatus?: ScannedPairingDeviceInfo['deviceStatus'];
  batteryLevel?: number;
  reason: ViQtorStatusWarningReason;
  rememberedSelection?: RememberedSelection;
};

export type PendingDeviceTypeConflict = {
  deviceId: string;
  deviceType: DeviceType;
  rememberedSelection?: RememberedSelection;
};

export function getAdditionalInfoDeviceType(deviceType: ScannedDeviceType): DeviceType {
  return deviceType === DeviceType.ViQtor ? DeviceType.ViQtor : DeviceType.Healthdot;
}

export async function clearRememberedSelection(): Promise<void> {
  try {
    await idbDel(LAST_SELECTION_STORAGE_KEY);
  } catch (err) {
    log('rememberedSelection/clear', err instanceof Error ? err : new Error(String(err)));
  }
}

export async function writeRememberedSelection(selection: RememberedSelection): Promise<void> {
  try {
    await idbSet(LAST_SELECTION_STORAGE_KEY, selection);
  } catch (err) {
    log('rememberedSelection/write', err instanceof Error ? err : new Error(String(err)));
  }
}

export async function getRememberedSelection(): Promise<RememberedSelection | undefined> {
  try {
    const selection = await idbGet<RememberedSelection>(LAST_SELECTION_STORAGE_KEY);
    if (!selection || Object.keys(selection).length === 0) return undefined;
    return selection;
  } catch (err) {
    log('rememberedSelection/read', err instanceof Error ? err : new Error(String(err)));
    return undefined;
  }
}

export function shouldConfirmViQtorBatteryStatus(
  deviceType: DeviceType,
  scannedDeviceBatteryLevel?: number,
): boolean {
  return (
    deviceType === DeviceType.ViQtor &&
    scannedDeviceBatteryLevel !== undefined &&
    scannedDeviceBatteryLevel < VIQTOR_BATTERY_WARNING_THRESHOLD
  );
}

export function getViQtorStatusWarningReason(
  deviceType: DeviceType,
  deviceInfo: ScannedPairingDeviceInfo | undefined,
): ViQtorStatusWarningReason | undefined {
  if (deviceType !== DeviceType.ViQtor || !deviceInfo) return undefined;

  if (
    shouldConfirmViQtorBatteryStatus(deviceType, deviceInfo.batteryLevel) ||
    deviceInfo.deviceStatus === 'BATTERY_LOW' ||
    deviceInfo.deviceStatus === 'BATTERY_CRITICAL'
  ) {
    return 'LOW_BATTERY';
  }

  if (deviceInfo.deviceStatus === 'DISCONNECTED') {
    return 'NO_CONNECTION';
  }

  // UNKNOWN is not evidence of a fault. It means the platform holds no telemetry for the
  // device at all, and it wipes a device's DeviceStatus whenever an assignment ends
  // (device-adapter DeviceService.endAssignment -> DeviceStatus.cleanup), so every re-used
  // viQtor reads as UNKNOWN until its next message. Warning on it stopped the wizard on
  // healthy hardware, so the pairing flow lets it through.
  return undefined;
}

export function shouldSkipAdditionalInfoStep(clinicalUnits: ClinicalUnit[]): boolean {
  return clinicalUnits.length === 0;
}
